/**
 * Frontend error telemetry beacon. Wires window.onerror and
 * unhandledrejection to POST /api/error-events. Uses navigator.sendBeacon
 * when available so the call survives page unload; falls back to
 * fetch({ keepalive: true }).
 *
 * Throttled to 20 events / 60s per tab to prevent runaway error loops
 * from DDOSing our own endpoint.
 */

type Source = 'chat-ui' | 'dashboard';

const THROTTLE_LIMIT = 20;
const THROTTLE_WINDOW_MS = 60_000;
const SECRET_KEY_PATTERN = /password|apikey|token|secret|authorization/i;

let configuredSource: Source | null = null;
let configuredBaseUrl = '/api';
let counterWindowStart = 0;
let counterCount = 0;
let initialized = false;
// Ambient context merged into every reported event. Set by long-lived UI
// state (e.g. the active agent) so EVERY error — including uncaught
// window.onerror events that have no call site to thread context through —
// records which agent was active. Per-call context wins on key conflicts.
let ambientContext: Record<string, unknown> = {};

function redactSensitive(value: unknown): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => redactSensitive(v));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = SECRET_KEY_PATTERN.test(k) ? '[REDACTED]' : redactSensitive(v);
  }
  return out;
}

function isThrottled(): boolean {
  const now = Date.now();
  if (now - counterWindowStart >= THROTTLE_WINDOW_MS) {
    counterWindowStart = now;
    counterCount = 1;
    return false;
  }
  counterCount += 1;
  return counterCount > THROTTLE_LIMIT;
}

interface BeaconPayload {
  source: string;
  severity: 'error' | 'warning';
  message: string;
  stack?: string;
  code?: string;
  context?: Record<string, unknown>;
  occurredAt: string;
}

function buildPayload(
  err: unknown,
  context: Record<string, unknown> | undefined,
  severity: 'error' | 'warning'
): BeaconPayload {
  let message = 'Unknown error';
  let stack: string | undefined;
  const code: string | undefined = undefined;
  if (err instanceof Error) {
    message = err.message || 'Error';
    stack = err.stack;
  } else if (typeof err === 'string') {
    message = err;
  } else {
    try {
      message = JSON.stringify(err);
    } catch {
      message = 'Unknown error';
    }
  }
  // `configuredSource` is guaranteed non-null here because reportError
   // early-returns when it isn't set — see the `if (!configuredSource)` guard
   // in reportError.
  // Ambient context (e.g. active agent) underlies per-call context so a
  // call site can override an ambient key, but every event still carries
  // the ambient fields. An empty merge stays `undefined` so events with no
  // context don't gain an empty object.
  const merged = { ...ambientContext, ...context };
  const hasContext = Object.keys(merged).length > 0;
  return {
    source: `frontend:${configuredSource}`,
    severity,
    message: message.slice(0, 4000),
    stack: stack?.slice(0, 8000),
    code,
    context: hasContext
      ? (redactSensitive(merged) as Record<string, unknown>)
      : undefined,
    occurredAt: new Date().toISOString(),
  };
}

function send(payload: BeaconPayload): void {
  try {
    const body = JSON.stringify(payload);
    const url = `${configuredBaseUrl}/error-events`;
    // Wrap as a typed Blob so sendBeacon sends Content-Type: application/json
    // (string overload would send text/plain, which express.json doesn't parse).
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      // No await; we don't care about the result.
    }).catch(() => {
      /* swallow — telemetry must never break the UI */
    });
  } catch {
    /* swallow */
  }
}

/**
 * Report an error to the dashboard. Non-blocking by design — telemetry
 * must never delay user-facing actions, never throw, and never propagate
 * a network failure back to the caller. The invariants below are
 * structural (enforced by the signature + implementation); preserve
 * them in any future change.
 *
 * Non-blocking invariants:
 * - Returns `void`. The signature makes `await reportError(...)` a
 *   TypeScript noop — there is nothing to await.
 * - Network call uses `navigator.sendBeacon` (queues to the UA, returns
 *   immediately) with a `fetch({ keepalive: true }).catch(() => {})`
 *   fallback. Neither path blocks the calling thread or surfaces a
 *   rejected promise.
 * - The entire send path is wrapped in a try/catch that swallows any
 *   synchronous failure (JSON.stringify on a circular structure, etc.).
 * - Throttled to TWENTY events per SIXTY seconds per tab, so a runaway
 *   loop cannot flood the endpoint nor block the UI re-rendering.
 *
 * Synchronous work on the calling thread is limited to building the
 * payload (`JSON.stringify` + the small `redactSensitive` traversal).
 * Message and stack are pre-bounded in `buildPayload`, so worst-case
 * cost stays bounded regardless of input.
 *
 * Callers: do NOT `await` this, chain `.then()`/`.catch()` on it, or
 * use its return value. Fire and continue.
 */
export function reportError(
  err: unknown,
  context?: Record<string, unknown>,
  severity: 'error' | 'warning' = 'error'
): void {
  if (!configuredSource) return; // not initialized
  if (isThrottled()) return;
  send(buildPayload(err, context, severity));
}

// Hold references to the registered listeners so tests (and any future
// teardown path) can remove them via _resetTelemetryForTesting. Without
// stored references, removeEventListener can't find the anonymous arrows
// and listeners would accumulate across test runs.
let registeredErrorListener: ((event: ErrorEvent) => void) | null = null;
let registeredRejectionListener:
  | ((event: PromiseRejectionEvent) => void)
  | null = null;

export function initErrorTelemetry(source: Source, baseUrl = '/api'): void {
  if (initialized) return;
  initialized = true;
  configuredSource = source;
  configuredBaseUrl = baseUrl;

  if (typeof window !== 'undefined') {
    registeredErrorListener = (event: ErrorEvent) => {
      reportError(event.error ?? event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };
    registeredRejectionListener = (event: PromiseRejectionEvent) => {
      reportError(event.reason ?? 'Unhandled promise rejection');
    };
    window.addEventListener('error', registeredErrorListener);
    window.addEventListener('unhandledrejection', registeredRejectionListener);
  }
}

/**
 * Set (or clear) the ambient context merged into every subsequent
 * reported event. Pass `null` to clear. Long-lived UI state — most
 * importantly the active agent — calls this so every error records which
 * agent was in play, without threading context through 50+ call sites or
 * losing it on uncaught window.onerror events.
 */
export function setTelemetryContext(
  context: Record<string, unknown> | null
): void {
  ambientContext = context ? { ...context } : {};
}

export function _resetTelemetryForTesting(): void {
  if (typeof window !== 'undefined') {
    if (registeredErrorListener) {
      window.removeEventListener('error', registeredErrorListener);
    }
    if (registeredRejectionListener) {
      window.removeEventListener(
        'unhandledrejection',
        registeredRejectionListener
      );
    }
  }
  registeredErrorListener = null;
  registeredRejectionListener = null;
  configuredSource = null;
  configuredBaseUrl = '/api';
  counterWindowStart = 0;
  counterCount = 0;
  initialized = false;
  ambientContext = {};
}
